import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import TrainingContentModal from '@/components/TrainingContentModal';
import FeedbackModal from '@/components/FeedbackModal';
import ReportingManagerDashboard from './ReportingManagerDashboard';
import ManagerEvaluationDashboard from './ManagerEvaluationDashboard';
import MarkTeamAttendanceSection from '@/components/MarkTeamAttendanceSection';
import LearningJourneyTracker from '@/components/LearningJourneyTracker';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Star, Heart, Play, BookOpen, Clock, ChevronLeft, ChevronRight, CheckCircle, Trophy, Medal, Award, TrendingUp, FileText, ClipboardCheck, Users, X, AlertCircle, UserCheck, GraduationCap, Lock, MoreVertical, Globe2, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default placeholder image for topics without thumbnails
const DEFAULT_THUMBNAIL = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=400&fit=crop";

// ─── Responsive Tab Navigation with Overflow Menu ────────────────────────────
const TAB_ITEMS = [
  { key: 'my-training', label: 'Course', icon: BookOpen },
  { key: 'team-progress', label: 'Team Progress', icon: TrendingUp },
  { key: 'evaluation', label: 'Evaluation', icon: FileText },
  { key: 'mark-attendance', label: 'Attendance', icon: ClipboardCheck },
];

const MOBILE_PRIMARY_TAB_LIMIT = 3;
const MOBILE_TAB_WIDTHS = [52, 104, 84, 96];
const DESKTOP_TAB_WIDTH = 132;
const MORE_TAB_WIDTH = 34;

const ManagerTabNav = ({ activeTab, setActiveTab, trainingMode, setTrainingMode }) => {
  const navRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(TAB_ITEMS.length);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef(null);

  // Measure available width and determine how many tabs fit
  const measureTabs = useCallback(() => {
    const container = navRef.current;
    if (!container) return;
    const containerWidth = container.offsetWidth;
    const isMobile = window.matchMedia?.('(max-width: 639px)')?.matches;
    if (isMobile) {
      setVisibleCount(Math.min(MOBILE_PRIMARY_TAB_LIMIT, TAB_ITEMS.length - 1));
      return;
    }
    const maxPrimaryTabs = TAB_ITEMS.length;
    const tabWidths = isMobile
      ? MOBILE_TAB_WIDTHS
      : TAB_ITEMS.map(() => DESKTOP_TAB_WIDTH);

    let nextVisibleCount = 1;
    for (let count = maxPrimaryTabs; count >= 1; count -= 1) {
      const needsMore = count < TAB_ITEMS.length;
      const requiredWidth = tabWidths.slice(0, count).reduce((sum, width) => sum + width, 0) + (needsMore ? MORE_TAB_WIDTH : 0);
      if (requiredWidth <= containerWidth) {
        nextVisibleCount = count;
        break;
      }
    }

    setVisibleCount(nextVisibleCount);
  }, []);

  useEffect(() => {
    measureTabs();
    const observer = new ResizeObserver(measureTabs);
    if (navRef.current) observer.observe(navRef.current);
    return () => observer.disconnect();
  }, [measureTabs]);

  // Close overflow menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setOverflowOpen(false);
      }
    };
    if (overflowOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [overflowOpen]);

  const visibleTabs = TAB_ITEMS.slice(0, visibleCount);
  const overflowTabs = TAB_ITEMS.slice(visibleCount);

  return (
    <div className="mb-6 border-b border-gray-200 overflow-visible max-w-full staff-mobile-nav-clean" ref={navRef}>
      <div className="flex items-center max-w-full min-w-0">
        {/* Visible tabs */}
        <div className="flex items-center gap-4 sm:gap-1 md:gap-4 min-w-0 max-w-full">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-3 px-0 sm:px-2 text-[13px] sm:text-sm font-semibold transition-colors relative whitespace-nowrap shrink-0 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent focus:outline-none focus:ring-0 shadow-none active:shadow-none ${
                  activeTab === tab.key
                    ? 'text-[#0056B3] border-b-2 border-[#0056B3]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="flex items-center gap-1 sm:gap-1.5">
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                  <span>{tab.label}</span>
                </div>
              </button>
            );
          })}

          {/* Overflow ⋯ menu */}
          {overflowTabs.length > 0 && (
            <div className="relative" ref={overflowRef}>
              <button
                onClick={() => setOverflowOpen(!overflowOpen)}
                className={`pb-3 px-0 sm:px-2 text-[13px] sm:text-sm font-semibold transition-colors relative whitespace-nowrap shrink-0 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent focus:outline-none focus:ring-0 shadow-none active:shadow-none ${
                  overflowTabs.some(t => t.key === activeTab)
                    ? 'text-[#0056B3]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="More tabs"
                aria-label="More tabs"
              >
                <div className="flex items-center">
                  <MoreVertical className="w-5 h-5" />
                </div>
              </button>
              {overflowOpen && (
                <div className="staff-mobile-tab-overflow-menu absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg py-1 z-50 min-w-[160px] max-w-[calc(100vw-1rem)] shadow-none">
                  {overflowTabs.map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => { setActiveTab(tab.key); setOverflowOpen(false); }}
                        className={`staff-mobile-dropdown-row w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent shadow-none ${
                          activeTab === tab.key
                            ? 'text-[#0056B3] font-semibold'
                            : 'text-gray-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

// ─── Responsive Chapter Filter Tabs with Overflow Menu ───────────────────────
const CHAPTER_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'untapped', label: 'Yet to Start' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'favorites', label: 'Favourite' },
];

const MOBILE_FILTER_WIDTHS = [42, 104, 100, 92, 86];
const DESKTOP_FILTER_WIDTH = 112;

const ChapterFilterTabs = ({ filterStatus, setFilterStatus, dashboardData }) => {
  const tabsRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(CHAPTER_FILTERS.length);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef(null);

  const measureTabs = useCallback(() => {
    const container = tabsRef.current;
    if (!container) return;
    const containerWidth = container.offsetWidth;
    const isMobile = window.matchMedia?.('(max-width: 639px)')?.matches;
    if (isMobile) {
      setVisibleCount(3);
      return;
    }
    const widths = isMobile ? MOBILE_FILTER_WIDTHS : CHAPTER_FILTERS.map(() => DESKTOP_FILTER_WIDTH);

    let nextVisibleCount = CHAPTER_FILTERS.length;
    for (let count = CHAPTER_FILTERS.length; count >= 1; count -= 1) {
      const needsMore = count < CHAPTER_FILTERS.length;
      const requiredWidth = widths.slice(0, count).reduce((sum, width) => sum + width, 0) + (needsMore ? MORE_TAB_WIDTH : 0);
      if (requiredWidth <= containerWidth) {
        nextVisibleCount = count;
        break;
      }
    }
    setVisibleCount(nextVisibleCount);
  }, []);

  useEffect(() => {
    measureTabs();
    const observer = new ResizeObserver(measureTabs);
    if (tabsRef.current) observer.observe(tabsRef.current);
    return () => observer.disconnect();
  }, [measureTabs]);

  useEffect(() => {
    const handleClick = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) setOverflowOpen(false);
    };
    if (overflowOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [overflowOpen]);

  const getCount = (key) => {
    const topics = dashboardData?.training_topics || [];
    if (key === 'untapped') return topics.filter(t => t.status === 'untapped' || !t.status).length;
    if (key === 'in_progress') return topics.filter(t => t.status === 'in_progress').length;
    if (key === 'completed') return topics.filter(t => t.status === 'completed').length;
    if (key === 'favorites') return topics.filter(t => t.is_favorite).length;
    return 0;
  };

  const visibleTabs = CHAPTER_FILTERS.slice(0, visibleCount);
  const overflowTabs = CHAPTER_FILTERS.slice(visibleCount);

  return (
    <div className="flex items-center min-w-0 flex-1 max-w-full overflow-visible staff-mobile-filter-clean" ref={tabsRef}>
      {visibleTabs.map(filter => (
        <button
          key={filter.key}
          onClick={() => setFilterStatus(filter.key)}
          className={`px-2 sm:px-3 py-2 text-[13px] font-semibold transition-colors border-b-2 whitespace-nowrap shrink-0 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent focus:outline-none focus:ring-0 shadow-none active:shadow-none ${
            filterStatus === filter.key
              ? 'border-[#0056B3] text-[#0056B3]'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          {filter.label}
          {filter.key !== 'all' && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100">
              {getCount(filter.key)}
            </span>
          )}
        </button>
      ))}

      {overflowTabs.length > 0 && (
        <div className="relative" ref={overflowRef}>
          <button
            onClick={() => setOverflowOpen(!overflowOpen)}
            className={`px-2 py-2 text-[13px] font-semibold transition-colors border-b-2 whitespace-nowrap shrink-0 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent focus:outline-none focus:ring-0 shadow-none active:shadow-none ${
              overflowTabs.some(t => t.key === filterStatus)
                ? 'border-[#0056B3] text-[#0056B3]'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
            title="More filters"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {overflowOpen && overflowTabs.length > 0 && (
            <div className="staff-mobile-filter-overflow-menu absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg py-1 z-50 min-w-[140px] max-w-[calc(100vw-1rem)] shadow-none">
              {overflowTabs.map(filter => (
                <button
                  key={filter.key}
                  onClick={() => { setFilterStatus(filter.key); setOverflowOpen(false); }}
                  className={`staff-mobile-dropdown-row w-full text-left px-4 py-2 text-[13px] flex items-center justify-between transition-colors bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent shadow-none ${
                    filterStatus === filter.key
                      ? 'text-[#0056B3] font-semibold'
                      : 'text-gray-700'
                  }`}
                >
                  {filter.label}
                  {filter.key !== 'all' && (
                    <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100">
                      {getCount(filter.key)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StaffDashboardPage = ({ isEmbedded = false, isManager = false }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('my-training');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [isReportingManager, setIsReportingManager] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Load saved language preference from localStorage
    return localStorage.getItem('userLanguagePreference') || 'English';
  });
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const hasSetInitialPage = React.useRef(false);
  const itemsPerPage = 6;
  const [showModal, setShowModal] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedTrainingTopicId, setSelectedTrainingTopicId] = useState(null);
  
  // Feedback modal state (for post-topic completion)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingFeedback, setPendingFeedback] = useState(null);
  
  // Offline topic modal state
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [offlineTopicInfo, setOfflineTopicInfo] = useState(null);
  
  // Hybrid topic modal state
  const [showHybridModal, setShowHybridModal] = useState(false);
  const [hybridTopicInfo, setHybridTopicInfo] = useState(null);
  
  // Flag to indicate hybrid assessment mode for TrainingContentModal
  const [isHybridAssessmentMode, setIsHybridAssessmentMode] = useState(false);
  const [hybridTopicName, setHybridTopicName] = useState('');
  
  // Training mode from hospital admin setting (fetched from backend)
  const [trainingMode, setTrainingMode] = useState('adaptive');
  const [assessmentMode, setAssessmentMode] = useState('chapter_wise');
  const [passingPercentage, setPassingPercentage] = useState(80);

  const getLanguageAbbreviation = (language) => {
    const normalized = String(language || '').trim().toLowerCase();
    if (normalized.startsWith('hin')) return 'HIN';
    if (normalized.startsWith('eng')) return 'ENG';
    return String(language || 'ENG').trim().slice(0, 3).toUpperCase();
  };

  const getLanguageDisplayName = (language) => {
    const normalized = String(language || '').trim().toLowerCase();
    if (normalized.startsWith('hin')) return 'हिंदी';
    if (normalized.startsWith('eng')) return 'English';
    return String(language || 'English');
  };

  // Tracker background from CMS
  const [trackerBgImage, setTrackerBgImage] = useState(null);
  
  useEffect(() => {
    let cancelled = false;

    const restoreAndLoad = async () => {
      const token = localStorage.getItem('token');
      let userData = localStorage.getItem('user');

      if (!token) {
        navigate('/signin');
        return;
      }

      if (!userData) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          userData = JSON.stringify(response.data);
          localStorage.setItem('user', userData);
        } catch (error) {
          console.error('[StaffDashboardPage] Failed to restore session:', error);
          navigate('/signin');
          return;
        }
      }

      if (cancelled) return;

      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Check role - be flexible with role check
      const userRole = parsedUser?.role;
      console.log('[StaffDashboardPage] User role:', userRole);
      
      if (!userRole || !['Staff', 'Reporting Manager', 'ReportingManager', 'Manager'].includes(userRole)) {
        console.error('[StaffDashboardPage] Access denied - invalid role:', userRole);
        toast.error(`Access denied - Invalid role: ${userRole || 'undefined'}`);
        navigate('/dashboard');
        return;
      }
      fetchLanguages();
      fetchDashboard();
      fetchLeaderboard();
      checkReportingManager();
      fetchTrackerBackground();
      fetchTrainingMode();
    };

    restoreAndLoad();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/training/languages`);
      setAvailableLanguages(response.data.languages || ['English', 'Hindi']);
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      setAvailableLanguages(['English', 'Hindi']);
    }
  };

  const fetchTrackerBackground = async () => {
    try {
      const response = await axios.get(`${API}/cms/tracker-background`);
      setTrackerBgImage(response.data.background_image || null);
    } catch (error) {
      console.error('Failed to fetch tracker background:', error);
    }
  };

  const fetchTrainingMode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff/training-mode`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingMode(response.data.training_mode || 'adaptive');
      setAssessmentMode(response.data.assessment_mode || 'chapter_wise');
      setPassingPercentage(response.data.passing_percentage || 80);
    } catch (error) {
      console.error('Failed to fetch training mode:', error);
    }
  };

  useEffect(() => {
    const handleFocus = () => {
      const shouldRefresh = localStorage.getItem('refreshDashboard');
      if (shouldRefresh === 'true') {
        localStorage.removeItem('refreshDashboard');
        fetchDashboard();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    const shouldRefresh = localStorage.getItem('refreshDashboard');
    if (shouldRefresh === 'true') {
      localStorage.removeItem('refreshDashboard');
      setTimeout(() => fetchDashboard(), 500);
    }
    
    return () => window.removeEventListener('focus', handleFocus);
  }, []);
  
  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      // Use average_feedback_rating from the dashboard API response (already included)
      if (data.training_topics && data.training_topics.length > 0) {
        data.training_topics = data.training_topics.map(topic => ({
          ...topic,
          feedback_rating: topic.average_feedback_rating || 0,
          feedback_count: 0 // Count not needed for display, can be fetched separately if required
        }));
      }
      
      setDashboardData(data);
    } catch (error) {
      toast.error('Failed to fetch dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const checkReportingManager = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/manager/team-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) setIsReportingManager(true);
    } catch (error) {
      setIsReportingManager(false);
    }
  };

  // Helper function to get translated title
  const getTranslatedTitle = (topic) => {
    // Check if topic has title_translations
    if (topic.title_translations && topic.title_translations[selectedLanguage]) {
      return topic.title_translations[selectedLanguage];
    }
    // Fallback to training_topic_name or title (backward compatibility)
    return topic.training_topic_name || topic.title;
  };

  const getSectionDurationMinutes = (section) => {
    const rawDuration =
      section?.content_duration ??
      section?.duration_minutes ??
      section?.content_duration_minutes ??
      section?.duration ??
      section?.langVersion?.content_duration ??
      section?.language_version?.content_duration;
    const minutes = Number(rawDuration);
    return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
  };

  const getTopicDurationMinutes = (topic) => {
    const explicitDuration = Number(topic?.assigned_duration_minutes ?? topic?.duration_minutes ?? topic?.total_duration_minutes);
    if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
      return Math.round(explicitDuration);
    }

    const assignedSections = topic?.training_sections || topic?.sections || [];
    const total = assignedSections.reduce((sum, section) => sum + getSectionDurationMinutes(section), 0);
    return Math.round(total);
  };

  const formatTopicDuration = (minutes) => {
    if (!minutes) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    return remaining ? `${hours}h ${remaining}m` : `${hours}h`;
  };
  
  const handleToggleFavorite = async (topicId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/staff/progress/update`, {
        training_topic_id: topicId,
        is_favorite: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? 'Removed from favorites' : 'Added to favorites');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  // Adaptive mode: determine if a topic is locked (sequential progression)
  const isTopicLockedInAdaptive = (topic) => {
    if (trainingMode !== 'adaptive') return false;
    const topics = dashboardData?.training_topics || [];
    const topicIndex = topics.findIndex(t => 
      (t.training_topic_id || t.id) === (topic.training_topic_id || topic.id)
    );
    if (topicIndex <= 0) return false; // First topic is never locked
    // Check all preceding topics are completed
    for (let i = 0; i < topicIndex; i++) {
      if (topics[i].status !== 'completed') return true;
    }
    return false;
  };

  const handleTopicClick = (topicId, trainingTopicId, topic = null) => {
    console.log('[StaffDashboardPage] handleTopicClick:', { topicId, trainingTopicId, trainingType: topic?.training_type });
    
    // Adaptive mode: enforce topic-level sequential locking
    if (topic && isTopicLockedInAdaptive(topic)) {
      toast.warning('Complete the current chapter to unlock the next.');
      return;
    }
    
    // Check if this is an offline topic
    if (topic && topic.training_type === 'offline') {
      // Show offline topic modal instead of content modal
      const isCompleted = topic.status === 'completed' || topic.completion_percentage === 100;
      setOfflineTopicInfo({
        topicId,
        topicName: topic.training_topic_name || topic.title,
        isCompleted,
        attendanceStatus: topic.attendance_status
      });
      setShowOfflineModal(true);
      return;
    }
    
    // Check if this is a hybrid topic
    if (topic && topic.training_type === 'hybrid') {
      const isCompleted = topic.status === 'completed' || topic.completion_percentage === 100;
      const attendanceMarked = topic.attendance_status === 'present';
      setHybridTopicInfo({
        topicId,
        trainingTopicId,
        topicName: topic.training_topic_name || topic.title,
        isCompleted,
        attendanceMarked,
        attendanceStatus: topic.attendance_status,
        completionPercentage: topic.completion_percentage || 0
      });
      setShowHybridModal(true);
      return;
    }
    
    // For online topics, open the content modal
    setSelectedTopicId(topicId);
    setSelectedTrainingTopicId(trainingTopicId);
    setShowModal(true);
  };
  
  // Handle starting assessment for hybrid topics
  const handleStartHybridAssessment = () => {
    if (hybridTopicInfo) {
      setShowHybridModal(false);
      // Set hybrid assessment mode flag before opening modal
      setIsHybridAssessmentMode(true);
      setHybridTopicName(hybridTopicInfo.topicName);
      // Open the training content modal which will show the assessment
      setSelectedTopicId(hybridTopicInfo.topicId);
      setSelectedTrainingTopicId(hybridTopicInfo.trainingTopicId);
      setShowModal(true);
    }
  };

  const handleCloseModal = (feedbackData = null) => {
    console.log('[StaffDashboardPage] handleCloseModal called with:', feedbackData);
    
    setShowModal(false);
    setSelectedTopicId(null);
    setSelectedTrainingTopicId(null);
    // Reset hybrid assessment mode
    setIsHybridAssessmentMode(false);
    setHybridTopicName('');
    
    // Check if feedback was submitted (indicated by refreshDashboard flag)
    const needsRefresh = localStorage.getItem('refreshDashboard') === 'true';
    if (needsRefresh) {
      console.log('[StaffDashboardPage] Feedback was submitted, clearing flag and refreshing dashboard');
      localStorage.removeItem('refreshDashboard');
    }
    
    // Add delay to ensure backend progress update completes before refreshing
    console.log('[StaffDashboardPage] Waiting for progress update to complete, then refreshing dashboard');
    setTimeout(() => {
      console.log('[StaffDashboardPage] Refreshing dashboard now');
      fetchDashboard();
    }, 500); // 500ms delay to ensure backend update completes
    
    // If feedback data is provided, show feedback modal after topic modal closes
    if (feedbackData) {
      console.log('[StaffDashboardPage] Setting pending feedback and will show modal after 300ms');
      setPendingFeedback(feedbackData);
      // Delay to ensure smooth transition between modals
      setTimeout(() => {
        console.log('[StaffDashboardPage] Opening feedback modal now');
        setShowFeedbackModal(true);
      }, 800); // Increased delay to account for dashboard refresh
    }
  };

  const getProgressPercentage = (topic) => {
    if (topic.status === 'completed') return 100;
    if (topic.completion_percentage) return Math.round(topic.completion_percentage);
    return 0;
  };

  const filterTopics = (topics) => {
    if (!topics) return [];
    
    switch (filterStatus) {
      case 'untapped':
        return topics.filter(t => t.status === 'untapped' || !t.status);
      case 'in_progress':
        return topics.filter(t => t.status === 'in_progress');
      case 'completed':
        return topics.filter(t => t.status === 'completed');
      case 'favorites':
        return topics.filter(t => t.is_favorite);
      default:
        return topics;
    }
  };

  const filteredTopics = filterTopics(dashboardData?.training_topics || []);
  const totalPages = Math.ceil(filteredTopics.length / itemsPerPage);
  const paginatedTopics = filteredTopics.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
    hasSetInitialPage.current = false;
  }, [filterStatus]);

  // Auto-navigate to the page containing the active topic on initial load
  useEffect(() => {
    if (hasSetInitialPage.current || !dashboardData?.training_topics?.length) return;
    hasSetInitialPage.current = true;
    const topics = filterTopics(dashboardData.training_topics);
    // Find active topic: first in_progress, then first untapped
    let activeIdx = topics.findIndex(t => t.status === 'in_progress');
    if (activeIdx === -1) activeIdx = topics.findIndex(t => t.status === 'untapped' || !t.status);
    if (activeIdx > 0) {
      const targetPage = Math.floor(activeIdx / itemsPerPage) + 1;
      if (targetPage > 1) setCurrentPage(targetPage);
    }
  }, [dashboardData]);

  if (loading) {
    const loadingContent = (
      <div className="p-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading your training dashboard...</p>
        </div>
      </div>
    );
    return isEmbedded ? loadingContent : <AdminLayout>{loadingContent}</AdminLayout>;
  }

  // Helper function for badge icons
  const getBadgeIcon = (badge) => {
    if (badge === 'Gold') return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (badge === 'Silver') return <Medal className="w-5 h-5 text-gray-400" />;
    if (badge === 'Bronze') return <Award className="w-5 h-5 text-orange-600" />;
    return null;
  };

  // Leaderboard sidebar content
  const leaderboardSidebar = (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-6 h-6 text-yellow-500" />
        <h2 className="text-lg font-bold text-[#0056B3]">Leaderboard</h2>
      </div>
      
      {loadingLeaderboard ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-3 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      ) : leaderboardData && leaderboardData.leaderboard.length === 0 ? (
        /* Empty state — no staff activity yet */
        <div className="text-center py-8 px-2">
          <div className="mx-auto mb-3 flex items-center justify-center w-14 h-14 rounded-full bg-blue-50">
            <Trophy className="w-7 h-7 text-[#0056B3]" />
          </div>
          <div className="flex items-end justify-center gap-1 mb-3" aria-hidden="true">
            <div className="w-4 h-4 rounded-t bg-gray-200" />
            <div className="w-4 h-7 rounded-t bg-blue-200" />
            <div className="w-4 h-3 rounded-t bg-gray-200" />
          </div>
          <p className="text-sm font-bold text-gray-900">Your leaderboard will appear here</p>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
            The leaderboard will appear once your {leaderboardData.institution_type || 'institution'} starts completing training.
          </p>
        </div>
      ) : leaderboardData ? (
        <div className="space-y-2">
          {/* Top 5 Leaderboard */}
          {leaderboardData.leaderboard.slice(0, 5).map((entry) => (
            <div 
              key={entry.user_id} 
              className={`border rounded-lg transition-all ${
                entry.user_id === leaderboardData.own_rank?.user_id 
                  ? 'bg-blue-50 border-[#0056B3] border-2' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              style={{ paddingTop: '14px', paddingBottom: '14px', paddingLeft: '18px', paddingRight: '18px' }}
            >
              <div className="flex items-start gap-3">
                <div className="w-7 flex-shrink-0 text-center pt-0.5">
                  {entry.badge ? (
                    getBadgeIcon(entry.badge)
                  ) : (
                    <span className="text-sm font-bold text-gray-400">#{entry.rank}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate">{entry.name}</p>
                  <p className="text-xs text-gray-600 mt-0.5 leading-tight truncate">{entry.department || 'N/A'}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-semibold text-[#0056B3]">{Math.round(entry.score)}%</p>
                </div>
              </div>
            </div>
          ))}
          
          {/* Show user's rank if not in top 5 */}
          {leaderboardData.own_rank && leaderboardData.own_rank.rank > 5 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600 mb-2 font-medium">Your Position:</p>
              <div className="bg-blue-50 border-2 border-[#0056B3] rounded-lg" style={{ paddingTop: '14px', paddingBottom: '14px', paddingLeft: '18px', paddingRight: '18px' }}>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-bold text-[#0056B3] flex-shrink-0 pt-0.5">#{leaderboardData.own_rank.rank}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-tight truncate">{leaderboardData.own_rank.name}</p>
                    <p className="text-xs text-gray-600 mt-0.5 leading-tight truncate">{leaderboardData.own_rank.department || 'N/A'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-semibold text-[#0056B3]">{Math.round(leaderboardData.own_rank.score)}%</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-600 text-center py-4">No leaderboard data available</p>
      )}
    </div>
  );

  const content = (
    <div className="staff-mobile-surface p-3 sm:p-4 md:p-6 lg:p-8 pb-2 md:pb-8 max-w-full overflow-x-hidden">
        {/* Manager Tabs - Only show for managers */}
        {isManager && (
          <ManagerTabNav
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            trainingMode={trainingMode}
            setTrainingMode={setTrainingMode}
          />
        )}

        {/* Conditionally render content based on active tab for managers */}
        {isManager && activeTab === 'team-progress' && (
          <ReportingManagerDashboard isEmbedded={true} />
        )}

        {isManager && activeTab === 'evaluation' && (
          <ManagerEvaluationDashboard isEmbedded={true} />
        )}

        {isManager && activeTab === 'mark-attendance' && (
          <MarkTeamAttendanceSection />
        )}

        {/* My Training topics - show for all staff or when My Training tab is active for managers */}
        {(!isManager || activeTab === 'my-training') && (
          <>
        {/* Header with Training Progress Card */}
        <div className="mb-2">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-2">
            {/* Left: Title (hidden since heading is inside tracker) */}

            {/* Training mode is institution-level, not shown to staff */}

            {/* Right: Training Progress Card - Compact Horizontal (hidden since tracker has progress summary) */}
          </div>
        </div>

        {/* Learning Journey Tracker (Metrometer) — shown IDENTICALLY for BOTH Adaptive and
            Conventional modes. The dashboard experience is the same in either mode; only the
            underlying learning workflow differs. The tracker itself renders the chapter sequence,
            current chapter, locked/unlocked chapters, completion status, and the summary card
            (Total / Completed / Remaining) in its header. */}
        {dashboardData?.training_topics?.length > 0 && (
          <LearningJourneyTracker
            topics={dashboardData.training_topics}
            onTopicClick={(contentId, trainingTopicId, topic) => handleTopicClick(contentId, trainingTopicId, topic)}
            getTranslatedTitle={getTranslatedTitle}
            trackerBgImage={trackerBgImage}
            title="My Learning Path"
            showLocks
          />
        )}

        {/* Filter Tabs */}
        <div className="mb-6 border-b border-gray-200 max-w-full overflow-visible">
          <div className="flex items-center justify-between gap-2 max-w-full min-w-0">
            <ChapterFilterTabs
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              dashboardData={dashboardData}
            />
            
            {/* Language Dropdown */}
            <div className="relative shrink-0 staff-mobile-language-clean">
                <button
                  type="button"
                  onClick={() => setShowLanguageDropdown((open) => !open)}
                  className="h-9 px-1.5 bg-transparent flex items-center gap-1.5 text-[13px] font-semibold text-gray-700 hover:bg-transparent focus:bg-transparent active:bg-transparent focus:outline-none focus:ring-0 transition-colors rounded-none shadow-none active:shadow-none"
                  aria-label="Select language"
                >
                  <Globe2 className="w-3.5 h-3.5 text-[#0056B3]" />
                  <span>{getLanguageAbbreviation(selectedLanguage)}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showLanguageDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-10"
                      onClick={() => {
                        setShowLanguageDropdown(false);
                      }}
                    />
                    <div className="staff-mobile-language-menu absolute right-0 top-full z-20 w-max min-w-[8.75rem] max-w-[calc(100vw-1rem)] mt-1 bg-white border border-gray-200 rounded-lg shadow-none py-1 max-h-60 overflow-y-auto">
                      {availableLanguages
                        .map(lang => (
                          <button
                            type="button"
                            key={lang}
                            onClick={() => {
                              // v1.1.78: Only refresh if language actually changed
                              const previousLanguage = selectedLanguage;
                              if (previousLanguage !== lang) {
                                setSelectedLanguage(lang);
                                // Save language preference to localStorage
                                localStorage.setItem('userLanguagePreference', lang);
                                setShowLanguageDropdown(false);
                                
                                // v1.1.78: Force page refresh to restore video autoplay behavior
                                // This ensures the first section video auto-plays after language switch
                                console.log('[Language Switch] Refreshing page for language change:', previousLanguage, '->', lang);
                                window.location.reload();
                              } else {
                                setShowLanguageDropdown(false);
                              }
                            }}
                            className={`staff-mobile-dropdown-row w-full min-h-11 px-3 py-2.5 text-left cursor-pointer hover:bg-transparent focus:bg-transparent active:bg-transparent text-[13px] flex items-center gap-2.5 shadow-none ${
                              selectedLanguage === lang ? 'text-[#0056B3] font-medium' : 'text-gray-800'
                            }`}
                          >
                            <Globe2 className="w-3.5 h-3.5 text-[#0056B3]" />
                            <span className="font-semibold whitespace-nowrap leading-none">{getLanguageDisplayName(lang)}</span>
                          </button>
                        ))}
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>

        {/* Training Cards Grid */}
        {paginatedTopics.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No training chapters found</h3>
            <p className="text-gray-600">
              {filterStatus === 'favorites' 
                ? 'You haven\'t marked any chapters as favorites yet.'
                : 'No chapters match the selected filter.'}
            </p>
          </Card>
        ) : (
          <>
            <div className="staff-course-grid grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5 mb-6">
              {paginatedTopics.map((topic, index) => {
                const progress = getProgressPercentage(topic);
                const isCompleted = topic.status === 'completed';
                const isLocked = isTopicLockedInAdaptive(topic);
                const topicDurationMinutes = getTopicDurationMinutes(topic);
                const topicDurationLabel = formatTopicDuration(topicDurationMinutes);
                
                return (
                  <Card 
                    key={topic.training_topic_id || topic.id} 
                    className={`staff-course-card flex flex-col sm:flex-row overflow-hidden transition-all min-w-0 ${isLocked ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg cursor-pointer'}`}
                    onClick={() => {
                      const contentId = topic.id || topic.content_id || topic.training_topic_id;
                      const trainingTopicId = topic.training_topic_id || topic.id;
                      handleTopicClick(contentId, trainingTopicId, topic);
                    }}
                  >
                    {/* Thumbnail */}
                    <div className="staff-course-thumb relative w-full sm:w-32 lg:w-36 h-36 sm:h-32 lg:h-36 flex-shrink-0 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center overflow-hidden">
                      {isLocked && (
                        <div className="absolute inset-0 z-10 bg-black/40 flex items-center justify-center">
                          <Lock className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <img 
                        src={topic.training_thumbnail_url || topic.thumbnail_url || DEFAULT_THUMBNAIL} 
                        alt={topic.training_topic_name || topic.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // If custom thumbnail fails to load, try default thumbnail
                          e.target.onerror = null;
                          e.target.src = DEFAULT_THUMBNAIL;
                        }}
                      />
                      
                      {/* Training Type Watermark Badge - Center-bottom overlay on thumbnail */}
                      {(topic.training_type === 'offline' || topic.training_type === 'hybrid') && (
                        <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center z-10">
                          <Badge className={
                            topic.training_type === 'offline' 
                              ? 'bg-amber-500 text-white text-xs font-bold shadow-lg border border-white' 
                              : 'bg-purple-500 text-white text-xs font-bold shadow-lg border border-white'
                          }>
                            {topic.training_type === 'offline' ? '📋 Offline' : '🎯 Hybrid'}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="staff-course-body flex-1 min-w-0 p-3 sm:p-4 flex flex-col justify-between">
                      <div>
                        {/* Title and Favorite */}
                        <div className="flex items-start justify-between gap-2 mb-2 min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 flex-1 min-w-0 line-clamp-2 break-words">
                            {getTranslatedTitle(topic)}
                          </h3>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFavorite(topic.training_topic_id || topic.id, topic.is_favorite);
                            }}
                            className="p-1 hover:bg-gray-100 rounded transition-colors shrink-0"
                          >
                            <Heart 
                              className={`w-4 h-4 ${
                                topic.is_favorite 
                                  ? 'fill-red-500 text-red-500' 
                                  : 'text-gray-400'
                              }`}
                            />
                          </button>
                        </div>

                        {/* Attendance Status for Offline/Hybrid topics */}
                        {(topic.training_type === 'offline' || topic.training_type === 'hybrid') && !isCompleted && (
                          <div className="mb-2">
                            {topic.attendance_status === 'present' ? (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Attendance Marked Present
                              </span>
                            ) : topic.attendance_status === 'absent' ? (
                              <span className="text-xs text-red-600 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Marked Absent - Contact Manager
                              </span>
                            ) : (
                              <span className="text-xs text-yellow-600 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Pending Attendance
                              </span>
                            )}
                          </div>
                        )}

                        {/* Rating - Show only for online topics */}
                        {topic.training_type !== 'offline' && topic.training_type !== 'hybrid' && (
                        <div className="flex items-center mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-3 h-3 ${
                                star <= (topic.feedback_rating || 0)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                          <span className="ml-1 text-xs text-gray-600">
                            ({topic.feedback_rating || 0})
                            {topic.feedback_count > 0 && (
                              <span className="ml-1">• {topic.feedback_count} reviews</span>
                            )}
                          </span>
                        </div>
                        )}

                      </div>

                      {/* Subchapters and Duration - Bottom aligned */}
                      <div className="mt-auto pt-2">
                        {topic.training_type !== 'offline' && topic.training_type !== 'hybrid' && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600 min-w-0">
                          <div className="flex items-center flex-shrink-0">
                            <BookOpen className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="whitespace-nowrap">{topic.training_sections?.length || topic.sections?.length || 0} Subchapters</span>
                          </div>
                          <span className="text-gray-300 hidden sm:inline">|</span>
                          <div className="flex items-center flex-shrink-0" title={`Duration: ${topicDurationLabel}`}>
                            <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="whitespace-nowrap">{topicDurationLabel}</span>
                          </div>
                        </div>
                        )}

                        {/* Offline Training Message */}
                        {topic.training_type === 'offline' && (
                          <div className="text-xs text-gray-500">
                            In-person training session
                          </div>
                        )}

                        {/* Hybrid Training Message */}
                        {topic.training_type === 'hybrid' && topic.attendance_status === 'present' && !isCompleted && (
                          <div className="text-xs text-blue-600">
                            ✓ Attendance confirmed - Assessment available
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Progress Circle / Play Button / Status Indicator */}
                    <div className={`staff-course-action w-full sm:w-24 lg:w-28 min-h-[72px] sm:min-h-0 py-3 sm:py-0 flex items-center justify-center shrink-0 ${topic.adaptive_ready ? 'bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 border-t sm:border-t-0 sm:border-l border-green-100' : 'bg-gray-50 border-t sm:border-t-0'}`}>
                      {isCompleted ? (
                        <div className="relative">
                          <svg className="w-[53px] h-[53px] transform -rotate-90">
                            <circle
                              cx="26.5"
                              cy="26.5"
                              r="22.8"
                              stroke="#E5E7EB"
                              strokeWidth="4.75"
                              fill="none"
                            />
                            <circle
                              cx="26.5"
                              cy="26.5"
                              r="22.8"
                              stroke="#10B981"
                              strokeWidth="4.75"
                              fill="none"
                              strokeDasharray={`${2 * Math.PI * 22.8}`}
                              strokeDashoffset="0"
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm font-bold text-green-600">100%</span>
                          </div>
                        </div>
                      ) : topic.training_type === 'offline' ? (
                        /* Offline topic - show completion percentage based on attendance */
                        topic.attendance_status === 'present' ? (
                          /* Attendance marked present = 100% complete */
                          <div className="relative">
                            <svg className="w-[53px] h-[53px] transform -rotate-90">
                              <circle
                                cx="26.5"
                                cy="26.5"
                                r="22.8"
                                stroke="#E5E7EB"
                                strokeWidth="4.75"
                                fill="none"
                              />
                              <circle
                                cx="26.5"
                                cy="26.5"
                                r="22.8"
                                stroke="#10B981"
                                strokeWidth="4.75"
                                fill="none"
                                strokeDasharray={`${2 * Math.PI * 22.8}`}
                                strokeDashoffset="0"
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-sm font-bold text-green-600">100%</span>
                            </div>
                          </div>
                        ) : topic.attendance_status === 'absent' ? (
                          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                            <Clock className="w-6 h-6 text-red-600" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                            <Clock className="w-6 h-6 text-amber-600" />
                          </div>
                        )
                      ) : topic.training_type === 'hybrid' ? (
                        /* Hybrid topic - show completion percentage based on attendance + assessment */
                        topic.attendance_status !== 'present' ? (
                          /* Attendance pending - 0% */
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                            <Clock className="w-6 h-6 text-purple-600" />
                          </div>
                        ) : (
                          /* Attendance done = 80% complete, show progress circle with assessment button */
                          <div className="relative cursor-pointer" onClick={(e) => {
                            e.stopPropagation();
                            const contentId = topic.id || topic.content_id || topic.training_topic_id;
                            const trainingTopicId = topic.training_topic_id || topic.id;
                            handleTopicClick(contentId, trainingTopicId, topic);
                          }}>
                            <svg className="w-[53px] h-[53px] transform -rotate-90">
                              <circle
                                cx="26.5"
                                cy="26.5"
                                r="22.8"
                                stroke="#E5E7EB"
                                strokeWidth="4.75"
                                fill="none"
                              />
                              <circle
                                cx="26.5"
                                cy="26.5"
                                r="22.8"
                                stroke="#9333EA"
                                strokeWidth="4.75"
                                fill="none"
                                strokeDasharray={`${2 * Math.PI * 22.8}`}
                                strokeDashoffset={`${2 * Math.PI * 22.8 * (1 - 0.8)}`}
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-sm font-bold text-purple-600">80%</span>
                            </div>
                          </div>
                        )
                      ) : progress > 0 ? (
                        <div className="relative">
                          <svg className="w-[53px] h-[53px] transform -rotate-90">
                            <circle
                              cx="26.5"
                              cy="26.5"
                              r="22.8"
                              stroke="#E5E7EB"
                              strokeWidth="4.75"
                              fill="none"
                            />
                            <circle
                              cx="26.5"
                              cy="26.5"
                              r="22.8"
                              stroke="#0056B3"
                              strokeWidth="4.75"
                              fill="none"
                              strokeDasharray={`${2 * Math.PI * 22.8}`}
                              strokeDashoffset={`${2 * Math.PI * 22.8 * (1 - progress / 100)}`}
                              strokeLinecap="round"
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-sm font-bold text-[#0056B3]">{progress}%</span>
                          </div>
                        </div>
                      ) : (
                        <button 
                          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${topic.adaptive_ready ? 'bg-green-600 hover:bg-green-700' : 'bg-[#0056B3] hover:bg-blue-700'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const contentId = topic.id || topic.content_id || topic.training_topic_id;
                            const trainingTopicId = topic.training_topic_id || topic.id;
                            handleTopicClick(contentId, trainingTopicId, topic);
                          }}
                        >
                          <Play className="w-6 h-6 text-white ml-1" fill="white" />
                        </button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="staff-course-pagination flex items-center justify-between border-t pt-6">
                <p className="text-sm text-gray-600 hidden md:block">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredTopics.length)} of {filteredTopics.length} chapters
                </p>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={currentPage === page ? 'bg-[#0056B3] hover:bg-blue-700' : ''}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        </>
        )}

      {/* Training Chapters Modal for Staff/Reporting Manager */}
      <TrainingContentModal 
        isOpen={showModal}
        onClose={handleCloseModal}
        contentId={selectedTopicId}
        trainingTopicId={selectedTrainingTopicId}
        selectedLanguage={selectedLanguage}
        isHybridAssessmentMode={isHybridAssessmentMode}
        hybridTopicName={hybridTopicName}
        institutionTrainingMode={trainingMode}
        institutionAssessmentMode={assessmentMode}
        institutionPassingPercentage={passingPercentage}
      />

      {/* Feedback Modal (appears after topic completion) */}
      {pendingFeedback && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => {
            setShowFeedbackModal(false);
            setPendingFeedback(null);
            // Refresh dashboard after feedback modal closes
            fetchDashboard();
          }}
          trainingTopicId={pendingFeedback.topicId}
          topicName={pendingFeedback.topicName}
          feedbackSet={pendingFeedback.feedbackSet}
          isMandatory={true}
          onSubmit={() => {
            setShowFeedbackModal(false);
            setPendingFeedback(null);
            toast.success('Thank you for your feedback!');
            fetchDashboard();
          }}
        />
      )}

      {/* Offline Chapter Information Modal */}
      {showOfflineModal && offlineTopicInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className={`px-6 py-5 ${offlineTopicInfo.isCompleted ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {offlineTopicInfo.isCompleted ? (
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-white" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-7 h-7 text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-white">Offline Training</h3>
                    <p className="text-sm text-white/80">In-person session</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowOfflineModal(false);
                    setOfflineTopicInfo(null);
                  }}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Chapter Name */}
              <div className="mb-5 pb-4 border-b border-gray-100">
                <p className="text-sm text-gray-500 mb-1">Chapter</p>
                <p className="text-lg font-semibold text-gray-900">{offlineTopicInfo.topicName}</p>
              </div>

              {offlineTopicInfo.isCompleted ? (
                /* Completed State */
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-100">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-800">Training Completed!</p>
                      <p className="text-sm text-green-600">This chapter is marked 100% completed.</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      You have successfully completed this offline training chapter. Your attendance has been recorded by your Reporting Manager.
                    </p>
                  </div>
                </div>
              ) : (
                /* Not Completed State */
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800">This is an Offline Chapter</p>
                      <p className="text-sm text-amber-600 mt-1">
                        The training is attended offline, so there is no content to complete here.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <UserCheck className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-blue-800">How to Complete</p>
                        <p className="text-sm text-blue-600 mt-1 leading-relaxed">
                          This chapter will be marked 100% completed once your <strong>Reporting Manager</strong> marks you as <strong>Present</strong>.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                    <Users className="w-4 h-4 text-gray-500" />
                    <p className="text-sm text-gray-600">
                      Please contact your Reporting Manager to mark your attendance.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6">
              <Button
                onClick={() => {
                  setShowOfflineModal(false);
                  setOfflineTopicInfo(null);
                }}
                className={`w-full py-3 text-white font-medium rounded-xl transition-colors ${
                  offlineTopicInfo.isCompleted 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-[#0056B3] hover:bg-[#004494]'
                }`}
              >
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hybrid Chapter Information Modal */}
      {showHybridModal && hybridTopicInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className={`px-6 py-5 ${
              hybridTopicInfo.isCompleted 
                ? 'bg-gradient-to-r from-green-500 to-green-600' 
                : hybridTopicInfo.attendanceMarked 
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {hybridTopicInfo.isCompleted ? (
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-7 h-7 text-white" />
                    </div>
                  ) : hybridTopicInfo.attendanceMarked ? (
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <GraduationCap className="w-7 h-7 text-white" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <AlertCircle className="w-7 h-7 text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-bold text-white">Hybrid Training</h3>
                    <p className="text-sm text-white/80">Offline + Assessment</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowHybridModal(false);
                    setHybridTopicInfo(null);
                  }}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Chapter Name */}
              <div className="mb-5 pb-4 border-b border-gray-100">
                <p className="text-sm text-gray-500 mb-1">Chapter</p>
                <p className="text-lg font-semibold text-gray-900">{hybridTopicInfo.topicName}</p>
              </div>

              {hybridTopicInfo.isCompleted ? (
                /* Fully Completed State (100%) */
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-100">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-800">Training Completed!</p>
                      <p className="text-sm text-green-600">This Hybrid chapter is 100% completed.</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      You have successfully completed this Hybrid training chapter. Your attendance was recorded and you passed the assessment.
                    </p>
                  </div>
                </div>
              ) : hybridTopicInfo.attendanceMarked ? (
                /* Attendance Done - Assessment Pending State (80%) */
                <div className="space-y-4">
                  {/* Progress Indicator */}
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-purple-800">Progress</span>
                      <span className="text-lg font-bold text-purple-600">80%</span>
                    </div>
                    <div className="w-full bg-purple-200 rounded-full h-3">
                      <div className="bg-purple-600 h-3 rounded-full" style={{ width: '80%' }}></div>
                    </div>
                    <p className="text-xs text-purple-600 mt-2">Attendance marked ✓ | Assessment pending</p>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-100">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-green-800">Attendance Confirmed!</p>
                      <p className="text-sm text-green-600 mt-1">
                        Your Reporting Manager has marked you as Present.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <GraduationCap className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-blue-800">Complete the Assessment</p>
                        <p className="text-sm text-blue-600 mt-1 leading-relaxed">
                          To complete the remaining <strong>20%</strong>, you must take the assessment and pass it.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <p className="text-sm text-gray-600">
                      After passing, this chapter will be 100% completed.
                    </p>
                  </div>
                </div>
              ) : (
                /* Attendance Not Marked Yet State */
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800">This is a Hybrid Chapter</p>
                      <p className="text-sm text-amber-600 mt-1">
                        The training is attended offline, so there is no course content to complete here.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ClipboardCheck className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-purple-800">How Hybrid Chapters Work</p>
                        <p className="text-sm text-purple-600 mt-1 leading-relaxed">
                          Completion requires: <strong>1)</strong> Attendance marked by your manager, then <strong>2)</strong> Passing the assessment.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl border border-red-100">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Clock className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-red-800">Attendance Required First</p>
                      <p className="text-sm text-red-600 mt-1 leading-relaxed">
                        You can take the assessment <strong>only after</strong> your Reporting Manager marks you as Present.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                    <Users className="w-4 h-4 text-gray-500" />
                    <p className="text-sm text-gray-600">
                      Please contact your Reporting Manager to mark your attendance.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6">
              {hybridTopicInfo.attendanceMarked && !hybridTopicInfo.isCompleted ? (
                /* Show Take Assessment button when attendance is done */
                <div className="space-y-3">
                  <Button
                    onClick={handleStartHybridAssessment}
                    className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <GraduationCap className="w-5 h-5" />
                    Take Assessment
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowHybridModal(false);
                      setHybridTopicInfo(null);
                    }}
                    className="w-full py-3 font-medium rounded-xl transition-colors"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => {
                    setShowHybridModal(false);
                    setHybridTopicInfo(null);
                  }}
                  className={`w-full py-3 text-white font-medium rounded-xl transition-colors ${
                    hybridTopicInfo.isCompleted 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-[#0056B3] hover:bg-[#004494]'
                  }`}
                >
                  Got it
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      
    </div>
  );

  return isEmbedded ? content : (
    <AdminLayout sidebarContent={leaderboardSidebar}>
      {content}
    </AdminLayout>
  );
};

export default StaffDashboardPage;
